import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPlay, FiActivity, FiShoppingCart, FiLogOut } from 'react-icons/fi';
import { NAV_GROUPS } from '../config/navConfig';
import SidebarGroup from './SidebarGroup';
import NavSearch from './NavSearch';
import AccountMenu from './AccountMenu';
import authService from '../services/authService';

// Off-canvas drawer — everything that isn't pinned to MobileTopBar: account menu,
// search, Start/Stop Production, Orders, and the full grouped nav.
export default function MobileDrawer({ open, onClose, accessibleItems, badgeValues, production, orders, onOpenChat }) {
  const location = useLocation();

  // Close on route change — mirrors the old Navbar's useEffect(..., [location])
  useEffect(() => { onClose(); }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const alwaysItems = accessibleItems.filter(i => i.group === 'always');

  const handleLogout = () => {
    authService.clearToken();
    window.location.href = '/';
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1049,
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity 0.2s ease',
        }}
      />
      <div
        className="d-lg-none d-flex flex-column"
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, width: 280, maxWidth: '85vw',
          background: 'var(--sidebar-bg)', zIndex: 1050, boxShadow: '4px 0 24px rgba(0,0,0,0.2)',
          transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease',
          overflowY: 'auto',
        }}
      >
        <div className="d-flex align-items-center justify-content-between" style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <AccountMenu onNavigate={onClose} align="left" />
        </div>

        <div style={{ padding: '10px 14px' }}>
          <NavSearch variant="drawer" accessibleItems={accessibleItems} onOpenChat={() => { onOpenChat(); onClose(); }} />
        </div>

        <div className="d-flex flex-column gap-2" style={{ padding: '0 14px 10px' }}>
          {production.canProduction && (
            <Link
              className={`btn btn-sm ${production.activeRunId ? 'btn-danger' : 'btn-success'} d-flex align-items-center gap-2 justify-content-center`}
              to={production.livePath}
              onClick={onClose}
            >
              {production.activeRunId ? <FiActivity size={14} /> : <FiPlay size={14} />}
              {production.activeRunId ? `Production in Progress — RUN #${production.activeRunId}` : 'Start Production'}
            </Link>
          )}
          {orders.visible && (
            <Link className="btn btn-sm btn-warning position-relative d-flex align-items-center gap-2 justify-content-center" to={orders.target} onClick={onClose}>
              <FiShoppingCart size={14} /> Orders
              {orders.badgeCount > 0 && (
                <span className="badge rounded-pill bg-danger" style={{ fontSize: 10 }}>{orders.badgeCount > 99 ? '99+' : orders.badgeCount}</span>
              )}
            </Link>
          )}
        </div>

        <div className="flex-fill" style={{ padding: '4px 0' }}>
          <SidebarGroup group={null} items={alwaysItems} collapsed={false} badgeValues={badgeValues} onItemClick={onClose} />
          {NAV_GROUPS.map(group => (
            <SidebarGroup
              key={group.key}
              group={group}
              items={accessibleItems.filter(i => i.group === group.key)}
              collapsed={false}
              badgeValues={badgeValues}
              onItemClick={onClose}
            />
          ))}
        </div>

        <button
          className="btn btn-outline-danger d-flex align-items-center justify-content-center gap-2 m-3"
          onClick={handleLogout}
        >
          <FiLogOut size={14} /> Logout
        </button>
      </div>
    </>
  );
}
